import { Button, InputNumber, Select } from 'antd';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
    ALL_TOKEN, codeTail, CodeRule, customCodes, defaultRule, isAllSelected, isHubDestination, MAX_COPIES_PER_CODE, normalizeCopies,
    numOptions, routeOptions, ruleCodes, segOptionsFor, toggleToken, warehouseOptions,
} from '../utils/sortCodeUtils';

interface SelectableChipProps {
    selected: boolean;
    onClick: () => void;
    /** 分拣码类 chip 用等宽字体 */
    mono?: boolean;
    children: ReactNode;
}

/** 可选中的小标签按钮（目的地、段数、配送仓 / 路区 / 编号） */
export const SelectableChip: React.FC<SelectableChipProps> = ({ selected, onClick, mono, children }) => (
    <button
        type="button"
        aria-pressed={selected}
        onClick={onClick}
        className={[
            'flex-none cursor-pointer whitespace-nowrap rounded-sm border border-solid px-2 py-0.5 text-xs leading-5',
            selected ? 'border-ant-purple-6 bg-[#f3eafe] text-ant-purple-6' : 'border-[#e5e6eb] bg-white text-[#4e5969] hover:border-ant-purple-4',
            mono ? 'font-mono' : '',
        ].join(' ')}
    >
        {children}
    </button>
);

interface CopiesStepperProps {
    value: number;
    onChange: (value: number) => void;
}

/** 每码包牌数步进器：− 输入 +，范围 1~MAX_COPIES_PER_CODE */
export const CopiesStepper: React.FC<CopiesStepperProps> = ({ value, onChange }) => (
    <span className="inline-flex flex-none items-center gap-0.5">
        <Button size="small" className="!w-5 !min-w-0 !px-0" onClick={() => onChange(Math.max(1, value - 1))}>−</Button>
        <InputNumber
            size="small"
            min={1}
            max={MAX_COPIES_PER_CODE}
            precision={0}
            controls={false}
            value={value}
            onChange={(v) => {
                // 清空输入时保持原值，避免把正在输入的内容回写成 1
                if (typeof v === 'number') onChange(normalizeCopies(v));
            }}
            className="!w-[38px] [&_input]:!text-center"
        />
        <Button size="small" className="!w-5 !min-w-0 !px-0" onClick={() => onChange(Math.min(MAX_COPIES_PER_CODE, value + 1))}>+</Button>
    </span>
);

interface CustomCodeRuleEditorProps {
    /** 批次目的地（流向），如 LA-SAN / EWR */
    destination: string;
    rules: CodeRule[];
    /** 目的地范围内可用的分拣码样本 */
    allCodes: string[];
    onChange: (rules: CodeRule[]) => void;
}

const FIELD_LABEL = 'w-[68px] flex-none text-right text-[11.5px] text-[#4e5969]';

/**
 * 创建包牌「自定义」规则编辑器（转写自原型 bagRowsNew 的 isHead / isRule 行）：
 * 每条定义选择段数，并按配送仓 → 路区 → 编号逐级多选 chip 收窄分拣码；至少保留 1 条定义。
 */
export const CustomCodeRuleEditor: React.FC<CustomCodeRuleEditorProps> = ({ destination, rules, allCodes, onChange }) => {
    const { t } = useTranslation();
    const k = (key: string) => `wms.sortingBatch.createBags.rule.${key}`;
    const uniqueCodes = customCodes(destination, rules, allCodes);

    const put = (index: number, patch: Partial<CodeRule>) => onChange(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));

    const renderRule = (rule: CodeRule, index: number) => {
        const codes = ruleCodes(destination, rule, allCodes);
        const copies = normalizeCopies(rule.copies);
        const allRoutes = isAllSelected(rule.routes);
        const whsValues = [ALL_TOKEN, ...warehouseOptions(destination, allCodes)];
        const routeValues = [ALL_TOKEN, ...routeOptions(destination, rule, allCodes)];
        const numValues = [ALL_TOKEN, ...numOptions(destination, rule, allCodes)];
        const range = codes.length > 1 ? `${codes[0]} … ${codes[codes.length - 1]}` : codes[0];
        return (
            <div key={index} className="flex flex-col gap-[7px] border-0 border-b border-solid border-[#f2f3f5] bg-[#fcfcfd] py-2.5 pl-6 pr-3">
                <div className="flex items-center gap-2.5">
                    <span className="text-xs font-medium text-[#1d2129]">{t(k('label'), { index: index + 1 })}</span>
                    <span className="text-[11px] text-ant-purple-6">{t(k('bagCount'), { count: codes.length * copies })}</span>
                    <div className="flex-1" />
                    {rules.length > 1 && (
                        <button
                            type="button"
                            onClick={() => onChange(rules.filter((_, i) => i !== index))}
                            className="cursor-pointer border-0 bg-transparent p-0 text-[11.5px] text-[#e34d59]"
                        >
                            {t(k('delete'))}
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <span className={FIELD_LABEL}>{t(k('seg'))}</span>
                    <Select
                        size="small"
                        className="!w-[90px]"
                        value={rule.seg}
                        options={segOptionsFor(destination).map((n) => ({ value: n, label: String(n) }))}
                        onChange={(seg: number) => put(index, { seg, warehouses: [ALL_TOKEN], routes: [ALL_TOKEN], nums: [ALL_TOKEN] })}
                    />
                </div>
                {rule.seg >= 2 && isHubDestination(destination) && (
                    <div className="flex items-start gap-2">
                        <span className={`${FIELD_LABEL} pt-1`}>{t(k('warehouses'))}</span>
                        <span className="flex flex-1 flex-wrap gap-[5px]">
                            {whsValues.map((v) => (
                                <SelectableChip
                                    key={v}
                                    mono
                                    selected={rule.warehouses.includes(v)}
                                    onClick={() => put(index, { warehouses: toggleToken(rule.warehouses, v), routes: [ALL_TOKEN], nums: [ALL_TOKEN] })}
                                >
                                    {v === ALL_TOKEN ? t(k('allWarehouses')) : v}
                                </SelectableChip>
                            ))}
                        </span>
                    </div>
                )}
                {rule.seg >= 3 && (
                    <div className="flex items-start gap-2">
                        <span className={`${FIELD_LABEL} pt-1`}>{t(k('routes'))}</span>
                        <span className="flex flex-1 flex-wrap gap-[5px]">
                            {routeValues.map((v) => (
                                <SelectableChip
                                    key={v}
                                    mono
                                    selected={rule.routes.includes(v)}
                                    onClick={() => put(index, { routes: toggleToken(rule.routes, v), nums: [ALL_TOKEN] })}
                                >
                                    {v === ALL_TOKEN ? t(k('allRoutes')) : codeTail(v)}
                                </SelectableChip>
                            ))}
                        </span>
                    </div>
                )}
                {rule.seg >= 4 && (
                    <div className="flex items-start gap-2">
                        <span className={`${FIELD_LABEL} pt-1`}>{t(k('nums'))}</span>
                        <span className="flex flex-1 flex-wrap items-center gap-[5px]">
                            {numValues.map((v) => (
                                <SelectableChip
                                    key={v}
                                    mono
                                    selected={rule.nums.includes(v)}
                                    onClick={() => put(index, { nums: toggleToken(rule.nums, v) })}
                                >
                                    {v === ALL_TOKEN ? t(k('allNums')) : codeTail(v)}
                                </SelectableChip>
                            ))}
                            {allRoutes && <span className="text-[11px] text-[#c9cdd4]">{t(k('numsNote'))}</span>}
                        </span>
                    </div>
                )}
                <div className="flex items-center gap-2">
                    <span className={FIELD_LABEL}>{t(k('codes'))}</span>
                    <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-[#86909c]">
                        {codes.length ? t(k('codeSummary'), { range, count: codes.length }) : t(k('noMatch'))}
                    </span>
                    <span className="whitespace-nowrap text-[11.5px] text-[#4e5969]">{t(k('copies'))}</span>
                    <CopiesStepper value={copies} onChange={(v) => put(index, { copies: v })} />
                </div>
            </div>
        );
    };

    return (
        <div>
            <div className="flex items-center gap-2.5 border-0 border-b border-solid border-[#f7f8fa] bg-[#f7f3ff] py-2 pl-6 pr-2.5 text-[11.5px] text-[#4e5969]">
                <span className="flex-1">{t(k('header'), { rules: rules.length, codes: uniqueCodes.length })}</span>
                <Button
                    size="small"
                    className="!border-[#e9dcff] !text-[11.5px] !text-ant-purple-6"
                    onClick={() => onChange([...rules, defaultRule(destination)])}
                >
                    {t(k('add'))}
                </Button>
            </div>
            {rules.map(renderRule)}
        </div>
    );
};
